import classNames from "classnames";
import moment from "moment";
import React, { FC, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import {
  AvatarStack,
  AvatarStackItem,
} from "../../../components/avatar/AvatarStack";
import StatusMenu from "../../../components/dropdown/StatusMenu";
import { getInitials } from "../../../constants/constants";
import {
  toUpdateWorkTaskModel,
  WorkTaskModel,
} from "../../../factories/work/model/work-task.model";
import { useAppSelector } from "../../../redux/redux";
import { useUpdateStatusWorkMutation } from "../../../slices/WorkSlice";
import {
  useGetWorkTasksQuery,
  useUpdateWorkTaskMutation,
  useUpdateWorkTaskStatusMutation,
} from "../../../slices/WorkTasksSlice";
import { useGetWorkTypeQuery } from "../../../slices/WorkTypeSlice";
import {
  formatTimerHoursMinutes,
  getWorkTaskBudgetSeconds,
  getWorkTaskTrackedSeconds,
} from "../../main/timer/timerTarget";
import { useToggleAllSubtasks } from "../../work/pages/details/widgets/work-tasks/useToggleAllSubtasks";

type Props = {
  workId: string;
  workTypeId: string;
  workStatusId?: string;
};

type WorkTaskExpandedRowProps = {
  workTask: WorkTaskModel;
  workTypeId: string;
  completedStatusId?: string;
  workId: string;
  workStatusId?: string;
  workTasks: WorkTaskModel[];
  index: number;
  length: number;
};

const getTaskAssigneeItems = (workTask: WorkTaskModel): AvatarStackItem[] => {
  if (workTask.jobTaskAssignees?.length) {
    return workTask.jobTaskAssignees.map((assignee) => ({
      id: assignee.assignedUserId,
      label: getInitials(
        assignee.assignedUser.userProfile.firstName,
        assignee.assignedUser.userProfile.lastName,
      ),
      name: `${assignee.assignedUser.userProfile.firstName || ""} ${
        assignee.assignedUser.userProfile.lastName || ""
      }`.trim(),
      color: assignee.assignedUser.userProfile.color,
    }));
  }

  return workTask.assignedUser
    ? [
        {
          id: workTask.assignedUser.id,
          label: getInitials(
            workTask.assignedUser.userProfile.firstName,
            workTask.assignedUser.userProfile.lastName,
          ),
          name: `${workTask.assignedUser.userProfile.firstName || ""} ${
            workTask.assignedUser.userProfile.lastName || ""
          }`.trim(),
          color: workTask.assignedUser.userProfile.color,
        },
      ]
    : [];
};

const formatTaskDate = (date: string): string => {
  if (!moment(date).isValid()) {
    return "-";
  }

  return moment(date).format("MMM DD, YYYY");
};

const getTaskTime = (
  workTask: WorkTaskModel,
): { trackedTime: string; budgetTime: string } => {
  const trackedSeconds = getWorkTaskTrackedSeconds(workTask.duration);
  const budgetSeconds = getWorkTaskBudgetSeconds(workTask.timeBudget);

  return {
    trackedTime: formatTimerHoursMinutes(trackedSeconds),
    budgetTime: formatTimerHoursMinutes(budgetSeconds),
  };
};

const WorkTaskExpandedRow: FC<WorkTaskExpandedRowProps> = ({
  workTask,
  workTypeId,
  completedStatusId,
  workId,
  workStatusId,
  workTasks,
  index,
  length,
}) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const { trackedTime, budgetTime } = getTaskTime(workTask);
  const [updateWorkTaskStatus, { isLoading: isUpdatingDone }] =
    useUpdateWorkTaskStatusMutation();
  const [updateWorkTask] = useUpdateWorkTaskMutation();
  const [updateStatusWork] = useUpdateStatusWorkMutation();

  const updateWorkStatusToCompleted = async () => {
    if (!completedStatusId || workStatusId === completedStatusId) {
      return;
    }

    await updateStatusWork({
      workId,
      statusId: completedStatusId,
      subStatusId: "",
    }).unwrap();
  };

  const handleChangeWorkTaskStatus = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextIsDone = event.target.checked;

    if (nextIsDone && !completedStatusId) {
      toast("Completed status was not found", {
        type: "error",
        pauseOnHover: false,
      });

      return;
    }

    const nextWorkTasks = workTasks.map((item) =>
      item.id === workTask.id ? { ...item, isDone: nextIsDone } : item,
    );

    try {
      await updateWorkTaskStatus({
        taskId: workTask.id,
        isDone: nextIsDone,
        updatedByUserId: user.id,
      }).unwrap();

      if (nextIsDone) {
        await updateWorkTask(
          toUpdateWorkTaskModel(workTask, {
            statusId: completedStatusId,
            subStatusId: null,
          }),
        ).unwrap();
      }

      if (nextWorkTasks.length && nextWorkTasks.every((task) => task.isDone)) {
        await updateWorkStatusToCompleted();
      }
    } catch {
      toast("Something went wrong", {
        type: "error",
        pauseOnHover: false,
      });
    }
  };

  return (
    <tr>
      <td className={"px-4 py-2"}>
        <div className={"flex min-w-0 items-center gap-2"}>
          <input
            type={"checkbox"}
            checked={workTask.isDone}
            disabled={isUpdatingDone}
            onChange={handleChangeWorkTaskStatus}
            className={
              "checkbox scale-75 rounded-[5px] border-gray-300 bg-white [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100 disabled:cursor-not-allowed disabled:opacity-60"
            }
          />
          <span
            className={classNames(
              "truncate font-semibold",
              workTask.isDone && "line-through",
            )}>
            {workTask.name}
          </span>
        </div>
      </td>
      <td className={"px-4 py-2"}>
        <StatusMenu
          workTask={workTask}
          workTypeId={workTypeId}
          length={length}
          index={index}
          variant={"small"}
        />
      </td>
      <td className={"px-4 py-2 font-semibold"}>
        {formatTaskDate(workTask.dueDate)}
      </td>
      <td className={"px-4 py-2 font-semibold"}>
        <span className={"text-[#6B7280]"}>{trackedTime}</span>
        <span className={"text-gray-800"}>{" | "}</span>
        <span className={"text-gray-800"}>{budgetTime}</span>
      </td>
      <td className={"px-4 py-2"}>
        <AvatarStack
          withPreview
          previewTitle={"Assignees:"}
          items={getTaskAssigneeItems(workTask)}
        />
      </td>
    </tr>
  );
};

export const WorkTasksExpandedTable: FC<Props> = ({
  workId,
  workTypeId,
  workStatusId,
}) => {
  const { data, isLoading } = useGetWorkTasksQuery({
    workId: workId || "",
  });
  const { data: workType } = useGetWorkTypeQuery({ id: workTypeId });

  const workTasks = data || [];
  const completedStatus = [...(workType?.serviceStatuses ?? [])].find(
    (status) => status.name === "Completed",
  );

  // Header checkbox ticks / unticks every subtask (Roadmap 1798), same rules
  // as the work item Summary.
  const { toggleAll, isBusy } = useToggleAllSubtasks({
    workId,
    workTypeId,
    workTasks,
  });
  const doneCount = workTasks.filter((task) => task.isDone).length;
  const allDone = workTasks.length > 0 && doneCount === workTasks.length;
  const someDone = doneCount > 0 && !allDone;
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someDone;
    }
  }, [someDone]);

  return (
    <div className={"w-full rounded-md border border-gray-200 bg-[#f8fdfe]"}>
      <table className={"w-full table-fixed text-left text-xs text-gray-800"}>
        <thead
          className={"border-b border-gray-200 bg-[#f8fdfe] text-[#9FA1AD]"}>
          <tr>
            <th className={"w-[32%] px-4 py-2 font-semibold"}>
              <div className={"flex items-center gap-2"}>
                <input
                  ref={headerCheckboxRef}
                  type={"checkbox"}
                  aria-label={
                    allDone || someDone
                      ? "Untick all subtasks"
                      : "Tick all subtasks"
                  }
                  checked={allDone}
                  disabled={isLoading || !workTasks.length || isBusy}
                  onChange={toggleAll}
                  className={
                    "checkbox scale-75 rounded-[5px] border-gray-300 bg-white [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100 disabled:cursor-not-allowed disabled:opacity-60"
                  }
                />
                <span>{"Subtask"}</span>
              </div>
            </th>
            <th className={"w-[18%] px-4 py-2 font-semibold"}>{"Status"}</th>
            <th className={"w-[18%] px-4 py-2 font-semibold"}>{"Due Date"}</th>
            <th className={"w-[18%] px-4 py-2 font-semibold"}>{"Time"}</th>
            <th className={"w-[14%] px-4 py-2 font-semibold"}>{"Assignee"}</th>
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={5} className={"py-8"}>
                <div className={"flex items-center justify-center gap-2"}>
                  <div
                    className={
                      "h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-[#7C66F0]"
                    }
                  />
                  <span className={"font-semibold text-[#6B7280]"}>
                    {"Loading subtasks..."}
                  </span>
                </div>
              </td>
            </tr>
          ) : !workTasks.length ? (
            <tr>
              <td colSpan={5} className={"px-4 py-6 text-center font-semibold"}>
                {"There are no subtasks"}
              </td>
            </tr>
          ) : (
            workTasks.map((workTask, index) => (
              <WorkTaskExpandedRow
                key={workTask.id}
                workTask={workTask}
                workTypeId={workTypeId}
                completedStatusId={completedStatus?.id}
                workId={workId}
                workStatusId={workStatusId}
                workTasks={workTasks}
                index={index}
                length={workTasks.length}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
